import classnames from 'classnames';
import { Link } from 'react-router-dom';
import SpeakerList from './components/SpeakerList/speaker-list';
import SupporterList from './components/SupporterList/supporter-list';
import AddToCalButton from '@components/AddToCalendar/add-to-calendar';
import Podium from '@components/Icons/podium.svg';
import Tag from '@components/Icons/tag.svg';
import Location from '@components/Icons/location.svg';
import ArrowUpRight from '@components/Icons/arrow-up-right.svg';
import { useMainContext } from '@contexts/MainContext';

function containsAny(arr1, arr2) {
	return arr1.some((item) => arr2.includes(item));
}

/**
 * This wrapper is used to wrap the title and the speaker list in a link if the link is not disabled.
 * Usage is as follows:
 *
 * @param args
 * @returns {JSX.Element}
 * @constructor
 */
const LinkedWrapper = (args) => {
	const { dispatch } = useMainContext();

	const {
		children,
		postId,
		linkDisabled,
		className,
		title = 'View talk details',
	} = args;

	return (
		<>
			{!linkDisabled && (
				<Link
					className={className}
					to={{
						pathname: 'talk',
						search: `?id=${postId}`,
						state: { fromDashboard: true },
					}}
					onClick={(e) => {
						dispatch({
							type: 'SET_SCROLL_VALUE',
							payload: window.scrollY,
						});
						dispatch({
							type: 'SET_SELECTED_TALK_ID',
							payload: postId,
						});
					}}
					title={title}
				>
					{children}
				</Link>
			)}
			{linkDisabled && <>{children}</>}
		</>
	);
};

const Title = (args) => {
	const { title, postId, linkDisabled } = args;
	return (
		<h3 className="cfs-talk-title">
			<LinkedWrapper postId={postId} linkDisabled={linkDisabled}>
				<>{title}</>
			</LinkedWrapper>
		</h3>
	);
};

const Talk = ({ talk = {} }) => {
	const { state } = useMainContext();
	const { selectedCatsTags } = state;

	const {
		title,
		time_start: TimeStart = '',
		time_end: TimeEnd = '',
		hide_time: HideTime = false,
		is_opening: IsOpeningTalk = false,
		link_disabled: LinkDisabled = false,
		info_description: InfoDescription = '',
		post_id: PostId,
		speakers = [],
		sponsors = [],
		taxonomies: { categories = [], tags = [], venues = [] },
		timezone_text: TimezoneText = '',
		custom_venue: CustomVenue = '',
		class_names: CustomClassNames = [],
	} = talk;

	// Make sure we have either a start or an end time in order to display the time.
	const MetaTime = () => {
		return (
			<>
				{!!(TimeStart || TimeEnd) && (
					<div className="cfs-talk-time">
						<span className="cfs-talk-time__start">
							{TimeStart}
						</span>
						{TimeStart && TimeEnd && (
							<span className="cfs-talk-time__separator">-</span>
						)}
						<span className="cfs-talk-time__end">{TimeEnd}</span>
						{TimezoneText && (
							<span className="cfs-talk-time__timezone">
								({TimezoneText})
							</span>
						)}
					</div>
				)}
			</>
		);
	};

	const categoryClasses = [];
	const tagClasses = [];
	const currentCatsTags = [];

	if (categories.length > 0) {
		categories.forEach((category) => {
			currentCatsTags.push(category.term_id);
			categoryClasses.push(`cfs-talk-category-${category.slug}`);
		});
	}

	if (tags.length > 0) {
		tags.forEach((tag) => {
			currentCatsTags.push(tag.term_id);
			tagClasses.push(`cfs-talk-tag-${tag.slug}`);
		});
	}

	/**
	 * Enable adding selected class if the talk contains a category of a tag that is selected
	 */
	// const selectedCatsTags = selectedTags.concat(selectedCategories);

	if (selectedCatsTags?.length > 0) {
		if (containsAny(currentCatsTags, selectedCatsTags)) {
			CustomClassNames.push(`cfs-talk-selected`);
		} else {
			CustomClassNames.splice(
				CustomClassNames.indexOf('cfs-talk-selected'),
				1,
			);
		}
	}

	// Make sure there are unique class names
	const UniqueClassNames = [...new Set(CustomClassNames)];

	const mainClasses = classnames([
		'cfs-talk',
		IsOpeningTalk ? 'cfs-talk--is-opening' : '',
		categoryClasses,
		tagClasses,
		UniqueClassNames,
	]);

	return (
		<div id={PostId} className={mainClasses}>
			{!IsOpeningTalk && (
				<div className="cfs-talk-meta">
					{!HideTime && <MetaTime />}
					{(venues || CustomVenue) && (
						<div className="cfs-talk__venues">
							<img
								className="cfs-talk-ui-icon"
								src={Location}
								alt="Location icon"
							/>{' '}
							{!CustomVenue &&
								venues.map((venue, index) => (
									<div className="venue-item" key={index}>
										<span className="venue-name">
											{venue.name}
										</span>
										{venue.location && (
											<span className="venue-location">
												{venue.location}
											</span>
										)}
									</div>
								))}
							{CustomVenue && <span>{CustomVenue}</span>}
						</div>
					)}
					{categories && (
						<div className="cfs-talk__categories">
							<img
								className="cfs-talk-ui-icon"
								src={Podium}
								alt="Podium icon"
							/>{' '}
							{categories.map((category, index) => (
								<span key={index}>{category.name}</span>
							))}
						</div>
					)}
					{tags && (
						<div className="cfs-talk__tags">
							{tags.map((tag, index) => (
								<div className={'tag'} key={index}>
									<img
										className="cfs-talk-ui-icon"
										src={Tag}
										alt="Hashtag icon"
									/>
									<span>{tag.name}</span>
								</div>
							))}
						</div>
					)}
					<AddToCalButton data={talk} />
				</div>
			)}
			<div className="cfs-talk-description">
				<Title
					title={title}
					linkDisabled={LinkDisabled}
					postId={PostId}
				/>
				<div className="cfs-talk-speaker-list">
					<LinkedWrapper
						postId={PostId}
						linkDisabled={LinkDisabled}
						className="cfs-talk-speaker-list__link"
					>
						<SpeakerList data={speakers} />
					</LinkedWrapper>
				</div>
				{sponsors && (
					<div className="cfs-talk-supporter-list">
						<SupporterList data={sponsors} />
					</div>
				)}
				{InfoDescription && (
					<div className="cfs-talk-info-description">
						{InfoDescription}
					</div>
				)}
				{!LinkDisabled && (
					<div className="cfs-navigate-to-talk">
						<LinkedWrapper
							postId={PostId}
							linkDisabled={LinkDisabled}
							className="cfs-navigate-to-talk__link"
						>
							<img
								className="cfs-talk-ui-icon"
								src={ArrowUpRight}
								alt="Arrow up right icon"
								width="16"
								height="16"
							/>
						</LinkedWrapper>
					</div>
				)}
			</div>
		</div>
	);
};

export default Talk;
